import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight, Zap, Palette, Rocket } from 'lucide-react';

interface WelcomeFlowProps {
  onComplete: () => void;
}

export default function WelcomeFlow({ onComplete }: WelcomeFlowProps) {
  const [step, setStep] = useState(1);
  const [websiteType, setWebsiteType] = useState('');
  const [experienceLevel, setExperienceLevel] = useState('');
  const navigate = useNavigate();

  const handleStart = () => {
    // Save selections to localStorage for AI wizard context
    localStorage.setItem('onboarding_type', websiteType);
    localStorage.setItem('onboarding_experience', experienceLevel);

    onComplete();
    navigate('/wizard?new=true');
  };

  if (step === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-500 via-purple-600 to-pink-500 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl">
              <Sparkles className="w-12 h-12 text-primary-600" />
            </div>
            <h1 className="text-5xl font-bold text-white mb-4">
              Welcome to Your AI Website Builder
            </h1>
            <p className="text-xl text-white/90">
              Create a beautiful website in under 3 minutes
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Zap className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">AI-Powered</h3>
                <p className="text-sm text-gray-600">
                  Describe your business, we build your site
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Palette className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">Beautiful Design</h3>
                <p className="text-sm text-gray-600">
                  Professional templates customized for you
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Rocket className="w-6 h-6 text-pink-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">Launch Fast</h3>
                <p className="text-sm text-gray-600">
                  Go live in minutes, not days
                </p>
              </div>
            </div>

            <button
              onClick={() => setStep(2)}
              className="w-full bg-gradient-to-r from-primary-500 to-purple-600 text-white font-semibold py-4 px-6 rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2 text-lg"
            >
              Get Started
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>

          <p className="text-center text-white/80 text-sm">
            Join thousands of users who built their websites with AI
          </p>
        </div>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-500 via-purple-600 to-pink-500 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Quick Setup</h2>
                <span className="text-sm text-gray-500">Step 1 of 2</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-primary-500 h-2 rounded-full" style={{ width: '50%' }} />
              </div>
            </div>

            <div className="mb-8">
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                What type of website are you building?
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { value: 'portfolio', label: 'Portfolio/Personal', icon: '👤' },
                  { value: 'business', label: 'Business/Corporate', icon: '🏢' },
                  { value: 'ecommerce', label: 'E-commerce/Shop', icon: '🛍️' },
                  { value: 'blog', label: 'Blog/Content', icon: '📝' },
                ].map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setWebsiteType(type.value)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      websiteType === type.value
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{type.icon}</span>
                      <span className="font-medium text-gray-900">{type.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-gray-700"
              >
                Back
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={!websiteType}
                className="flex-1 bg-primary-500 text-white font-semibold py-3 px-6 rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                Continue
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 3) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-500 via-purple-600 to-pink-500 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Almost there!</h2>
                <span className="text-sm text-gray-500">Step 2 of 2</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-primary-500 h-2 rounded-full" style={{ width: '100%' }} />
              </div>
            </div>

            <div className="mb-8">
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                What's your experience level with building websites?
              </label>
              <div className="space-y-3">
                {[
                  { value: 'beginner', label: 'First time building a website', desc: 'I need guidance and templates' },
                  { value: 'intermediate', label: 'Some experience', desc: 'I know the basics and want to customize' },
                  { value: 'expert', label: 'Expert', desc: 'I want full control and advanced features' },
                ].map((level) => (
                  <button
                    key={level.value}
                    onClick={() => setExperienceLevel(level.value)}
                    className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                      experienceLevel === level.value
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium text-gray-900 mb-1">{level.label}</div>
                    <div className="text-sm text-gray-600">{level.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(2)}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-gray-700"
              >
                Back
              </button>
              <button
                onClick={handleStart}
                disabled={!experienceLevel}
                className="flex-1 bg-gradient-to-r from-primary-500 to-purple-600 text-white font-semibold py-3 px-6 rounded-lg hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                Start Building
                <Rocket className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
